import Decimal from "decimal.js";
import { MONTHLY } from "../constants/frequencies";
import { FIXED_LOAN_AMOUNT } from "../constants/payment_type";
import { REFINANCE } from "../constants/document_types";
import { NON_QM } from "../constants/loan_types";
import { INTEREST_ONLY } from "../constants/loan_payment_type";
import { NONQM_DSCR } from "../constants/income_calculator_types";
import { dscr_total_monthly_rents } from "./income";
import {
  loan_amount_first_mortgage,
  first_mortgage_payment_p_and_i,
  property_tax
} from "./loan";
import {
  home_owners_insurance_monthly,
  flood_insurance_monthly,
  hoa_dues_1,
  hoa_dues_2,
  mello_roos
} from "./property";

// Two-way write-back for the DSCR calculator. The LO edits a line item on the DSCR
// page (Income page or the Forms form) and it flows to that value's SOURCE page, so
// the whole app stays in sync. All DSCR escrow entry is MONTHLY, and we force the
// frequency dropdown to Monthly so no quarterly/annual conversion is needed (saves
// the LO time and removes a class of calculation error).

// Subject-property escrow fields (shared across all loans) -> New Property /
// Subject Property page.
export function dscrPropertyPatch(documentType, field, value) {
  switch (field) {
    case "tax":
      // Refi and Purchase store property tax in DIFFERENT fields. Enter monthly,
      // force the frequency to Monthly.
      return documentType === REFINANCE
        ? {
            refinance_property_tax_amount: value,
            refinance_property_tax_frequency: MONTHLY
          }
        : {
            property_tax_type: "amount",
            property_tax_annual_amount: value,
            property_tax_amount_frequency: MONTHLY
          };
    case "hoi":
      return {
        home_owners_insurance: value,
        home_owners_insurance_frequency: MONTHLY
      };
    case "flood":
      return {
        flood_insurance: value,
        flood_insurance_frequency: MONTHLY
      };
    default:
      return {};
  }
}

// Per-loan fields -> LE Price Eng (rate, HOA) / Down Payment (loan amount). Rate and
// Loan Amount are written to their source; the Down Payment / Loan Calculator page
// then derives the P&I, which the DSCR reads back. HOA/Mello/Other consolidates into
// HOA 1 (monthly) so the single field the LO typed is what shows.
export function dscrLoanPatch(documentType, loan, field, value) {
  switch (field) {
    case "rate":
      return { rate: value };
    case "hoa":
      return { hoa1: value, hoa1_frequency: MONTHLY, hoa2: "0", mello_roos_amount: "0" };
    case "loanAmount":
      // Refi reads the 1st-lien amount from the Loan Calculator's computed total;
      // Purchase uses a fixed loan amount + down-payment type on the Down Payment
      // page. Write to whichever the app reads back.
      return documentType === REFINANCE
        ? {
            refinance_loan_calculator: {
              ...((loan && loan.refinance_loan_calculator) || {}),
              total_of_new_first_loan: value
            }
          }
        : { down_payment_type: FIXED_LOAN_AMOUNT, fixed_loan_amount: value };
    default:
      return {};
  }
}

// HOA/Mello/Other write-back. hoa_dues reads loan.hoa1 when the loan's property type
// is an HOA type, otherwise property.hoa1 — so write BOTH (monthly, others zeroed) so
// the DSCR edit always applies regardless of the read path. HOA is a subject-property
// carrying cost, so sharing it across the loan scenarios is correct for DSCR.
export function dscrHoaPropertyPatch(value) {
  return {
    hoa1: value,
    hoa1_frequency: MONTHLY,
    hoa2: "0",
    hoa2_frequency: MONTHLY,
    mello_roos_amount: "0",
    mello_roos_frequency: MONTHLY
  };
}

// Property address two-way write-back: the DSCR calc reads/writes the SUBJECT
// property address (property.address / city / state / zip) — the true source — so a
// change on the DSCR form or Income page flows to the New Property / Subject
// Property page. The "City, State  Zip" line is parsed back into its three fields.
export function dscrAddressStreetPatch(value) {
  return { address: value };
}
export function dscrAddressCszPatch(value) {
  const parts = (value || "").split(",");
  const city = (parts[0] || "").trim();
  const rest = (parts.slice(1).join(",") || "").trim().split(/\s+/).filter(Boolean);
  const state = rest[0] || "";
  const zip = rest[1] || "";
  return { city, state, zip };
}

// ── DSCR-loan detection + qualifying ratios (per loan) ────────────────────────
// A DSCR loan qualifies on the subject property's rent-to-PITIA coverage, not the
// borrower's DTI. The underwriter only cares about the DSCR ratio, so on the
// disclosures (Down Payment / Loan Calculator, Summary Sheet, LE page 1) a DSCR
// loan REPLACES its DTI figure(s) with the Subject Property PITIA DSCR (or ITIA
// DSCR when that loan is Interest-Only). This is PER LOAN — a standard Fannie Mae
// 1-4 unit loan in the same file still shows real DTI.

// The NonQM DSCR income source is file-level (one rent roll for the subject
// property), so every DSCR loan in the file shares the same rents.
function dscr_income_source(document) {
  const borrowers =
    (document && document.applicant && document.applicant.borrowers) || [];
  for (const borrower of borrowers) {
    const sources = (borrower.income && borrower.income.sources) || [];
    for (const source of sources) {
      if (source && source.calculator === NONQM_DSCR) {
        return source;
      }
    }
  }
  return null;
}

export function file_has_dscr_income(document) {
  return !!dscr_income_source(document);
}

export function dscr_document_monthly_rents(document) {
  const source = dscr_income_source(document);
  return source ? dscr_total_monthly_rents(source) : new Decimal(0);
}

// A loan is a DSCR loan when its Loan Type is Non-QM AND the file carries a NonQM
// DSCR income source (the DSCR calculator was selected on the Income page). This
// matches the LO's path: Loan Type = Non-QM > Income > DSCR Calc.
export function is_dscr_loan(document, loan) {
  return !!loan && loan.type === NON_QM && file_has_dscr_income(document);
}

// PITIA / ITIA DSCR for ONE loan, using the SAME math as the DSCR income
// calculator so every surface agrees. DSCR = total monthly rents ÷ monthly PITIA
// (or ITIA). ITIA uses an interest-only payment computed unconditionally
// (loan amount × rate ÷ 12) so the ratio is correct even when the app's
// interest-only payment helper would return 0.
export function dscr_ratios_for_loan(documentType, document, loan) {
  const { property, property_to_be_sold, is_selling_property } = document;
  const totalRents = dscr_document_monthly_rents(document);
  const z = new Decimal(0);
  try {
    const args = [documentType, loan, property, property_to_be_sold, is_selling_property];
    const loanAmount = loan_amount_first_mortgage(...args);
    const pi = first_mortgage_payment_p_and_i(...args);
    const io = new Decimal(loanAmount)
      .mul(new Decimal(loan.rate || 0).div(100))
      .div(12);
    const escrow = property_tax(documentType, loan, property)
      .add(home_owners_insurance_monthly(documentType, property))
      .add(flood_insurance_monthly(documentType, property))
      .add(hoa_dues_1(property, loan))
      .add(hoa_dues_2(property, loan))
      .add(mello_roos(property, loan));
    const pitia = pi.add(escrow);
    const itia = io.add(escrow);
    return {
      totalRents,
      pitia,
      itia,
      pitiaDscr: pitia.gt(0) ? totalRents.div(pitia) : z,
      itiaDscr: itia.gt(0) ? totalRents.div(itia) : z,
      interestOnly: loan.payment_type === INTEREST_ONLY
    };
  } catch (e) {
    return {
      totalRents,
      pitia: z,
      itia: z,
      pitiaDscr: z,
      itiaDscr: z,
      interestOnly: loan.payment_type === INTEREST_ONLY
    };
  }
}
